import {Component, EventEmitter, OnInit} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { finalize, take } from 'rxjs';

// PrimeNG
import { CardModule } from 'primeng/card';
import { AvatarModule } from 'primeng/avatar';
import { DividerModule } from 'primeng/divider';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { Tooltip } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';

// App
import { UserService } from '../../services/user.service';
import { User } from '../../contracts/user.interface';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { Chip } from 'primeng/chip';
import { PasswordFieldsComponent } from '../../../shared/components/password-fields/password-fields.component';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    AvatarModule,
    DividerModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    LoaderComponent,
    Tooltip,
    Chip,
    PasswordFieldsComponent,
  ],
  providers: [UserService, MessageService],
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent implements OnInit {
  static avatarChanged: EventEmitter<string> = new EventEmitter<string>();

  user: User | null = null;
  profileForm!: FormGroup;

  loading = false;
  saving = false;
  editMode = false;

  selectedFile: File | null = null;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.profileForm = this.fb.group(
      {
        firstName: [''],
        lastName: [''],
        username: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        password: ['', Validators.minLength(6)],
        confirmPassword: ['']
      },
      { validators: this.passwordMatchValidator }
    );

    this.profileForm.disable();
    this.loadUserProfile();
  }

  private loadUserProfile(): void {
    this.loading = true;
    this.userService
      .getCurrentUser()
      .pipe(take(1), finalize(() => (this.loading = false)))
      .subscribe({
        next: (user) => {
          this.user = user;
          this.profileForm.patchValue({
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            username: user.username || '',
            email: user.email || ''
          });
        },
        error: () => this.showError('Failed to load user profile')
      });
  }

  toggleEditMode(): void {
    this.editMode = !this.editMode;

    if (this.editMode) {
      this.profileForm.enable();
    } else {
      this.profileForm.disable();
      if (this.user) {
        this.profileForm.patchValue({
          firstName: this.user.firstName || '',
          lastName: this.user.lastName || '',
          username: this.user.username || '',
          email: this.user.email || '',
          password: '',
          confirmPassword: ''
        });
      }
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];

      const reader = new FileReader();
      reader.onload = () => {
        if (this.user) this.user.imageMediaUrl = reader.result as string;
      };
      reader.readAsDataURL(this.selectedFile);
      this.uploadAvatar();
    }
  }

  private uploadAvatar(): void {
    if (!this.selectedFile || !this.user) return;

    const formData = new FormData();
    formData.append('file', this.selectedFile);

    this.saving = true;
    this.userService
      .upload(this.user!.id!, formData)
      .pipe(take(1), finalize(() => (this.saving = false)))
      .subscribe({
        next: (url) => {
          this.user!.imageMediaUrl = url;
          ProfileComponent.avatarChanged.emit(url);

          const storedUser = JSON.parse(localStorage.getItem('userData') || '{}');
          storedUser.imageMediaUrl = url;
          localStorage.setItem('userData', JSON.stringify(storedUser));

          this.showSuccess('Avatar aggiornato');
        },
        error: () => this.showError('Errore nel caricamento dell\'avatar')
      });
  }

  saveProfile(): void {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }

    const { confirmPassword, password, ...rest } = this.profileForm.value;

    const updatedData: Partial<User> = {
      ...rest,
      ...(password ? { password } : {})
    };

    this.saving = true;
    this.userService
      .updateCurrentUser(updatedData)
      .pipe(take(1), finalize(() => (this.saving = false)))
      .subscribe({
        next: () => {
          this.userService.getCurrentUser().pipe(take(1)).subscribe(data => {
            this.user = data;
            this.profileForm.patchValue({
              firstName: data.firstName || '',
              lastName: data.lastName || '',
              username: data.username,
              email: data.email
            });
          });
          this.toggleEditMode();
          this.showSuccess('Profilo aggiornato');
        },
        error: () => this.showError('Errore nel salvataggio del profilo')
      });
  }

  private passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const pass = group.get('password')?.value;
    const confirm = group.get('confirmPassword')?.value;
    if (pass) {
      return pass !== confirm ? { passwordMismatch: true } : null;
    }
    return null;
  }

  private showSuccess(detail: string) {
    this.messageService.add({ severity: 'success', summary: 'Successo', detail });
  }

  private showError(detail: string) {
    this.messageService.add({ severity: 'error', summary: 'Errore', detail });
  }
}
